import { InfoCard } from '@backstage/core-components';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import { makeStyles } from '@material-ui/core/styles';
import { ReactNode, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useBatchEditorAdmin } from '../../permissions/useBatchEditorAdmin';
import { BatchSwapBoardMappingDialog } from './BatchSwapBoardMappingDialog';
import { buildCatalogBoardSources } from './buildCatalogBoardSources';
import { CatalogBoardSummary, CatalogGapComponent, InactiveJiraMapping, JiraProjectOption } from './jiraBoardTypes';
import { UpdateBoardMappingDialog } from './UpdateBoardMappingDialog';

export type { CatalogGapComponent, InactiveJiraMapping } from './jiraBoardTypes';

type CatalogGapsPanelProps = {
  untaggedComponents: CatalogGapComponent[];
  inactiveMappings: InactiveJiraMapping[];
  /** Full inactive mapping list for batch swap sources (not search-filtered). */
  catalogInactiveMappings: InactiveJiraMapping[];
  loading: boolean;
  error: Error | undefined;
  searchActive: boolean;
  inactiveCheckUnavailable: boolean;
  jiraProjects: JiraProjectOption[];
  catalogSummaries: CatalogBoardSummary[];
};

type ParsedComponentRef = {
  entityRef: string;
  name: string;
  namespace: string;
  catalogPath: string;
  canUpdateMapping: boolean;
  ownerGroupRef?: string;
};

type GapTableRow = ParsedComponentRef & {
  projectKeys?: string[];
  staleProjectKeys?: string[];
  ownerGroupRef?: string;
};

type InactiveGapTableRow = ParsedComponentRef & {
  projectKeys: string[];
  staleProjectKeys: string[];
};

const useStyles = makeStyles(theme => ({
  nestedAccordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
    '&:before': {
      display: 'none',
    },
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  clickableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  rowLink: {
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  openLink: {
    display: 'inline-flex',
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  navigateCell: {
    width: 40,
    color: theme.palette.text.secondary,
  },
  actionCell: {
    width: 132,
    whiteSpace: 'nowrap',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  batchActionButton: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  cardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  unverifiedChip: {
    borderStyle: 'dashed',
  },
}));

function parseComponentRef(component: CatalogGapComponent): ParsedComponentRef | undefined {
  const [kindPart, namePart] = component.entityRef.split(':');
  if (kindPart?.toLowerCase() !== 'component' || !namePart) {
    return undefined;
  }
  const [namespace, name] = namePart.split('/');
  if (!namespace || !name) {
    return undefined;
  }
  return {
    entityRef: component.entityRef,
    name,
    namespace,
    catalogPath: `/catalog/${namespace}/component/${name}`,
    canUpdateMapping: component.canUpdateMapping,
    ownerGroupRef: component.ownerGroupRef,
  };
}

const accordionTransition = { unmountOnExit: true, timeout: 120 };

function groupRefToCatalogPath(entityRef: string): string | undefined {
  const [, namePart] = entityRef.split(':');
  if (!namePart) {
    return undefined;
  }
  const [namespace, name] = namePart.split('/');
  if (!namespace || !name) {
    return undefined;
  }
  return `/catalog/${namespace}/group/${name}`;
}

function formatGroupRefLabel(entityRef: string): string {
  const [, namePart] = entityRef.split(':');
  return namePart?.split('/').pop() ?? entityRef;
}

function ComponentGapTable({
  rows,
  emptyMessage,
  showProjectKeys = false,
  showOwnerGroup = false,
  jiraProjects,
  canUpdateBoard,
  allowRowUpdates = true,
  markProjectKeysUnverified = false,
}: {
  rows: GapTableRow[];
  emptyMessage: string;
  showProjectKeys?: boolean;
  showOwnerGroup?: boolean;
  jiraProjects: JiraProjectOption[];
  canUpdateBoard: boolean;
  allowRowUpdates?: boolean;
  markProjectKeysUnverified?: boolean;
}) {
  const classes = useStyles();
  const [dialogRow, setDialogRow] = useState<GapTableRow | undefined>();

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Namespace</TableCell>
              {showOwnerGroup ? <TableCell>Owner group</TableCell> : null}
              {showProjectKeys ? (
                <TableCell>
                  {markProjectKeysUnverified ? 'Unverified project keys' : 'Stale project keys'}
                </TableCell>
              ) : null}
              {canUpdateBoard && allowRowUpdates ? (
                <TableCell className={classes.actionCell}>Update</TableCell>
              ) : null}
              <TableCell className={classes.navigateCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const updateDisabled =
                jiraProjects.length === 0 || !row.canUpdateMapping;
              const updateButton = (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={updateDisabled}
                  onClick={() => setDialogRow(row)}
                >
                  Update board
                </Button>
              );

              return (
                <TableRow key={row.entityRef} hover className={classes.clickableRow}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={row.catalogPath}
                      className={classes.rowLink}
                      variant="body2"
                    >
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {row.namespace}
                    </Typography>
                  </TableCell>
                  {showOwnerGroup ? (
                    <TableCell>
                      {row.ownerGroupRef ? (
                        (() => {
                          const ownerPath = groupRefToCatalogPath(row.ownerGroupRef);
                          return ownerPath ? (
                            <Link component={RouterLink} to={ownerPath} variant="body2">
                              {formatGroupRefLabel(row.ownerGroupRef)}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {formatGroupRefLabel(row.ownerGroupRef)}
                            </Typography>
                          );
                        })()
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  ) : null}
                  {showProjectKeys ? (
                    <TableCell>
                      <Box className={classes.chipRow}>
                        {(row.staleProjectKeys ?? row.projectKeys ?? []).map(key => (
                          <Chip
                            key={key}
                            label={key}
                            size="small"
                            variant="outlined"
                            className={markProjectKeysUnverified ? classes.unverifiedChip : undefined}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  ) : null}
                  {canUpdateBoard && allowRowUpdates ? (
                    <TableCell className={classes.actionCell}>
                      {!row.canUpdateMapping ? (
                        <Tooltip title="Missing github.com/project-slug annotation required to open a catalog-info.yaml PR">
                          <span>{updateButton}</span>
                        </Tooltip>
                      ) : (
                        updateButton
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell className={classes.navigateCell} align="right">
                    <Link
                      component={RouterLink}
                      to={row.catalogPath}
                      className={classes.openLink}
                      aria-label={`Open ${row.name} in catalog`}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {dialogRow ? (
        <UpdateBoardMappingDialog
          open
          onClose={() => setDialogRow(undefined)}
          entityRef={dialogRow.entityRef}
          componentName={dialogRow.name}
          currentProjectKeys={dialogRow.projectKeys ?? []}
          staleProjectKeys={dialogRow.staleProjectKeys ?? []}
          jiraProjects={jiraProjects}
        />
      ) : null}
    </>
  );
}

function GapSection({
  title,
  countLabel,
  children,
}: {
  title: string;
  countLabel: string;
  children: ReactNode;
}) {
  const classes = useStyles();

  return (
    <Accordion
      className={classes.nestedAccordion}
      defaultExpanded={false}
      TransitionProps={accordionTransition}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" gridGap={8}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="body2" color="textSecondary">
            {countLabel}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block', paddingTop: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export function CatalogGapsPanel({
  untaggedComponents,
  inactiveMappings,
  catalogInactiveMappings,
  loading,
  error,
  searchActive,
  inactiveCheckUnavailable,
  jiraProjects,
  catalogSummaries,
}: CatalogGapsPanelProps) {
  const classes = useStyles();
  const { allowed: canUpdateBoard } = useBatchEditorAdmin();
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  const catalogBoardSources = useMemo(
    () => buildCatalogBoardSources(catalogSummaries, catalogInactiveMappings),
    [catalogSummaries, catalogInactiveMappings],
  );

  const batchSwapButton = canUpdateBoard ? (
    <Tooltip title="Batch swap Jira board mappings">
      <IconButton
        aria-label="Batch swap Jira board mappings"
        className={classes.batchActionButton}
        color="primary"
        size="small"
        onClick={() => setBatchDialogOpen(true)}
      >
        <PlaylistAddCheckIcon />
      </IconButton>
    </Tooltip>
  ) : null;

  const gapsCardTitle = (
    <Box className={classes.cardTitleRow}>
      <span>Catalog coverage gaps</span>
      {batchSwapButton}
    </Box>
  );

  const untaggedRows = untaggedComponents
    .map(parseComponentRef)
    .filter((row): row is ParsedComponentRef => row !== undefined);

  const inactiveRows: InactiveGapTableRow[] = inactiveMappings
    .map(item => {
      const parsed = parseComponentRef(item);
      if (!parsed) {
        return undefined;
      }
      return {
        ...parsed,
        projectKeys: item.allProjectKeys,
        staleProjectKeys: item.projectKeys,
      };
    })
    .filter((row): row is InactiveGapTableRow => row !== undefined);

  if (loading) {
    return (
      <InfoCard title={gapsCardTitle}>
        <Typography variant="body2" color="textSecondary">
          Loading component linkage…
        </Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title={gapsCardTitle}>
        <Typography variant="body2" color="textSecondary">
          Could not load catalog linkage.
        </Typography>
      </InfoCard>
    );
  }

  const totalGaps = untaggedComponents.length + inactiveMappings.length;

  return (
    <>
      <InfoCard
        title={gapsCardTitle}
        subheader={`${totalGaps} component(s) need Jira catalog attention`}
      >
      <GapSection
        title="Without resolvable Jira board"
        countLabel={`${untaggedComponents.length} component(s)`}
      >
        <ComponentGapTable
          rows={untaggedRows}
          showOwnerGroup
          jiraProjects={jiraProjects}
          canUpdateBoard={canUpdateBoard}
          allowRowUpdates
          emptyMessage={
            searchActive
              ? 'No unmapped components match your search.'
              : 'Every catalog component has an explicit or inherited Jira board.'
          }
        />
      </GapSection>

      <GapSection
        title="Mapped to inactive or unknown projects"
        countLabel={`${inactiveMappings.length} component(s)`}
      >
        {inactiveCheckUnavailable ? (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Could not compare against active Jira projects. Listed mappings could not be validated
            against Jira.
          </Typography>
        ) : null}
        <ComponentGapTable
          rows={inactiveRows}
          showProjectKeys
          markProjectKeysUnverified={inactiveCheckUnavailable}
          jiraProjects={jiraProjects}
          canUpdateBoard={canUpdateBoard}
          allowRowUpdates={!inactiveCheckUnavailable}
          emptyMessage={
            searchActive
              ? 'No stale mappings match your search.'
              : 'No components reference inactive, deprecated, or unknown Jira project keys.'
          }
        />
      </GapSection>

      <Box style={{ marginTop: 12 }}>
        <Typography variant="caption" color="textSecondary">
          Use the component name or arrow to open in the catalog. Right-click either link to open
          in a new tab.
          {canUpdateBoard
            ? ' Update board requires github.com/project-slug and queues a draft PR via the Batch Editor.'
            : null}
        </Typography>
      </Box>
      </InfoCard>

      <BatchSwapBoardMappingDialog
        open={batchDialogOpen}
        onClose={() => setBatchDialogOpen(false)}
        catalogSummaries={catalogBoardSources}
        jiraProjects={jiraProjects}
      />
    </>
  );
}
